import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

// Processing phases in order
const PHASES = [
  { key: 'image_processing_status', label: 'Processing Image' },
  { key: 'analysis_status', label: 'Analyzing' },
  { key: 'analysis_embedding_status', label: 'Indexing Analysis' },
  { key: 'enrichment_status', label: 'Enriching' },
  { key: 'enrichment_embedding_status', label: 'Indexing Enrichment' },
];

const DEFAULT_MAX_POLL_INTERVAL = 30000; // cap for backoff
const DEFAULT_OVERALL_TIMEOUT = 12 * 60 * 1000; // 12 minutes
const DEFAULT_MAX_CONSECUTIVE_EMPTY = 3; // stop after N straight {jobs:[]} responses

// Calculate progress percentage based on phase statuses
function calculateProgress(job) {
  if (!job) return { progress: 0, currentPhase: null };

  let completedPhases = 0;
  let currentPhase = null;

  for (const phase of PHASES) {
    const status = job[phase.key];
    if (status === 'complete' || status === 'skipped') {
      completedPhases++;
    } else if (status === 'in_progress') {
      currentPhase = phase.label;
      // Add half credit for in-progress phase
      completedPhases += 0.5;
      break;
    } else if (status === 'pending') {
      currentPhase = phase.label;
      break;
    } else if (status === 'failed') {
      currentPhase = `${phase.label} Failed`;
      break;
    }
  }

  const progress = Math.round((completedPhases / PHASES.length) * 100);
  return { progress, currentPhase };
}

// Snapshot of the fields that indicate real progress, used to detect a
// stale/unchanging job so we can back off instead of polling every 2s forever.
function snapshotOf(jobData) {
  if (!jobData) return null;
  return [
    jobData.status,
    ...PHASES.map((phase) => jobData[phase.key]),
  ].join('|');
}

/**
 * Hook to poll processing status for an uploaded item.
 *
 * @param {string} jobId - The processing job ID
 * @param {object} options - Options
 * @param {number} options.pollInterval - Base polling interval in ms while progress is changing (default: 2000)
 * @param {number} options.maxPollInterval - Cap for backed-off polling interval in ms (default: 30000)
 * @param {boolean} options.enabled - Whether polling is enabled (default: true)
 * @param {number} options.maxRetries - Max consecutive errors before giving up (default: 5)
 * @param {number} options.overallTimeout - Total time in ms to keep polling before giving up (default: 12 minutes)
 * @param {number} options.maxConsecutiveEmpty - Stop after this many consecutive empty {jobs:[]} responses (default: 3)
 * @param {function} options.onComplete - Callback when processing completes
 * @param {function} options.onError - Callback when processing fails
 */
export function useProcessingStatus(jobId, options = {}) {
  const {
    pollInterval = 2000,
    maxPollInterval = DEFAULT_MAX_POLL_INTERVAL,
    enabled = true,
    maxRetries = 5,
    overallTimeout = DEFAULT_OVERALL_TIMEOUT,
    maxConsecutiveEmpty = DEFAULT_MAX_CONSECUTIVE_EMPTY,
    onComplete,
    onError,
  } = options;

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Why polling stopped without the job reaching complete/failed: null while
  // still polling, otherwise 'timeout' or 'empty'.
  const [stopReason, setStopReason] = useState(null);
  // Bumped by retry() to reset all polling state and re-arm the effect.
  const [retryNonce, setRetryNonce] = useState(0);

  // Use refs for callbacks to avoid re-triggering effect
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  onCompleteRef.current = onComplete;
  onErrorRef.current = onError;

  const fetchStatus = useCallback(async () => {
    if (!jobId) return null;

    const response = await api.getProcessingStatus(jobId);
    // API returns { jobs: [...], total: N } - get first job
    const jobData = response.jobs?.[0] || null;
    return jobData;
  }, [jobId]);

  const retry = useCallback(() => {
    setRetryNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!jobId || !enabled) {
      setLoading(false);
      return;
    }

    let mounted = true;
    let timeoutId = null;
    let consecutiveErrors = 0;
    let consecutiveEmpty = 0;
    let staleStreak = 0; // consecutive polls where the job's status fields haven't changed
    let lastSnapshot = null;
    const startTime = Date.now();

    // Reset stop/loading state on (re)start so a retry() or new jobId re-arms cleanly
    setStopReason(null);
    setLoading(true);

    const poll = async () => {
      if (Date.now() - startTime >= overallTimeout) {
        if (mounted) setStopReason('timeout');
        return;
      }

      try {
        const jobData = await fetchStatus();

        if (!mounted) return;

        // Reset error count on success
        consecutiveErrors = 0;

        if (!jobData) {
          // Nothing in flight for this job id (yet, or ever again)
          consecutiveEmpty++;
          setJob(null);
          setLoading(false);
          setError(null);

          if (consecutiveEmpty >= maxConsecutiveEmpty) {
            setStopReason('empty');
            return;
          }

          timeoutId = setTimeout(poll, pollInterval);
          return;
        }

        consecutiveEmpty = 0;
        setJob(jobData);
        setLoading(false);
        setError(null);

        // Check if processing is complete or failed
        if (jobData.status === 'complete') {
          onCompleteRef.current?.(jobData);
          return; // Stop polling
        }

        if (jobData.status === 'failed') {
          onErrorRef.current?.(jobData);
          return; // Stop polling
        }

        // Back off once the job's status fields stop changing between polls,
        // but stay snappy (base pollInterval) while real progress is happening.
        const snapshot = snapshotOf(jobData);
        staleStreak = snapshot === lastSnapshot ? staleStreak + 1 : 0;
        lastSnapshot = snapshot;

        const nextInterval = Math.min(
          pollInterval * Math.pow(2, staleStreak),
          maxPollInterval
        );
        timeoutId = setTimeout(poll, nextInterval);
      } catch (err) {
        if (!mounted) return;

        consecutiveErrors++;
        setError(err.message);
        setLoading(false);

        // Stop polling after max retries
        if (consecutiveErrors >= maxRetries) {
          onErrorRef.current?.({ status: 'failed', error_message: `Polling failed after ${maxRetries} retries` });
          return;
        }

        // Retry with exponential backoff (capped at maxPollInterval)
        const backoffMs = Math.min(pollInterval * Math.pow(2, consecutiveErrors - 1), maxPollInterval);
        timeoutId = setTimeout(poll, backoffMs);
      }
    };

    poll();

    return () => {
      mounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [jobId, enabled, pollInterval, maxPollInterval, maxRetries, overallTimeout, maxConsecutiveEmpty, fetchStatus, retryNonce]);

  const { progress, currentPhase } = calculateProgress(job);
  const isComplete = job?.status === 'complete';
  const isFailed = job?.status === 'failed';

  return {
    job,
    loading,
    error,
    progress,
    currentPhase,
    isComplete,
    isFailed,
    isProcessing: job?.status === 'processing' || job?.status === 'pending',
    // True once polling has given up without the job reaching complete/failed
    // (overall timeout, or repeated {jobs:[]} responses). The caller should
    // show a "still processing - check back" state instead of a spinner.
    isTimedOut: stopReason !== null,
    stopReason,
    // Resets all backoff/timeout state and resumes polling immediately.
    retry,
  };
}

export default useProcessingStatus;
